import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import Button from '../ui/Button';
import { usePurchaseOrderAccess } from '../../hooks/usePurchaseOrderAccess';

/**
 * Purchase orders are their own section, off the Dashboard, and belong to no
 * module. Two kinds of people open them: someone the organisation's admin has
 * authorised (Organisation Admin → Purchase orders), who raises, sends and
 * receives an order, and an Accounts seat, who reads it to match a supplier's
 * invoice. `raising` is the new-order page, which Accounts cannot use.
 *
 * What each can do is decided inside the pages, and again by the server.
 */
export default function PurchaseOrdersGate({ raising = false, children }: {
  raising?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { canRaise, canRead, loading } = usePurchaseOrderAccess();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (canRaise || (canRead && !raising)) return <>{children}</>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
        <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-neutral-800 mb-1">Not authorised for purchase orders</h2>
        <p className="text-sm text-neutral-500 mb-4 break-words">
          {canRead
            ? 'You can read purchase orders, but raising one needs your organisation’s admin to authorise you.'
            : 'Your organisation’s admin chooses who can raise purchase orders.'}{' '}
          They do that on the Organisation Admin page, under Purchase orders.
        </p>
        <Button variant="outline" size="sm" onClick={() => navigate(canRead ? '/purchase-orders' : '/dashboard')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {canRead ? 'Back to purchase orders' : 'Back to the dashboard'}
        </Button>
      </div>
    </div>
  );
}
